const mapPlanFromApi = (plan) => {
  if (!plan) return null;
  
  const frequencyMap = {
    'daily': 'Daily',
    'weekly': 'Weekly',
    'monthly': 'Monthly',
    'quarterly': 'Quarterly',
    'yearly': 'Yearly'
  };
  
  const pricingTypeMap = {
    'fixed': 'Fixed',
    'dynamic': 'Variable'
  };
  
  const statusMap = {
    'active': 'Active',
    'inactive': 'Inactive',
    'cancelled': 'Cancelled'
  };
  
  return {
    planId: plan.plan_id,
    name: plan.name,
    description: plan.description,
    pricingType: pricingTypeMap[plan.pricing_type] || 'Fixed',
    amount: plan.amount,
    currency: plan.currency,
    frequency: frequencyMap[plan.billing_frequency] || 'Monthly',
    billingDay: plan.billing_day,
    billingMonth: plan.billing_month,
    duration: plan.billing_cycles,
    unlimitedDuration: !plan.billing_cycles,
    trialPeriodDays: plan.trial_period_days,
    status: statusMap[plan.status] || 'Active',
    bulkUpdateLastProcessedAt: plan.bulk_update_last_processed_at,
    redirectUrl: plan.redirect_url,
    merchantId: plan.merchant_id,
    createdAt: plan.created_at,
    updatedAt: plan.updated_at,
    activeSubscriptionsCount: plan.active_subscriptions_count,
    totalSubscriptions: plan.active_subscriptions_count,
    pausedSubscriptionsCount: plan.paused_subscriptions_count,
    cancelledSubscriptionsCount: plan.cancelled_subscriptions_count,
    subscriptionUrl: plan.subscription_url,
    paymentLink: plan.subscription_url,
    planImage: {
      url: plan.image_url || null,
      color: plan.background_color || null
    },
    productImageUrl: plan.image_url,
    backgroundColor: plan.background_color,
    planPrice: {
      amount: plan.amount,
      currency: plan.currency
    }
  };
};

export const getPlans = (token) => (payload) => ({
  endpoint: '/api/v1/recurring-payments/plans',
  method: 'GET',
  params: payload,
  headers: {
    'X-Authentication-Token': token,
  },
  cache: false,
  proxy: false,
  skipErrorDisplay: true,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data || {};
    const plansArray = Array.isArray(apiData.data) ? apiData.data : [];
    const mappedData = plansArray.map(mapPlanFromApi);
    
    // El backend puede devolver pagination en dos formatos:
    // 1. apiData.pagination.total_count (formato con objeto pagination)
    // 2. apiData.total_count (formato directo)
    const pagination = apiData.pagination || apiData;
    
    return {
      data: mappedData,
      pagination: {
        total_count: parseInt(pagination.total_count) || parseInt(pagination.totalCount) || 0,
        limit: parseInt(pagination.limit) || 10,
        offset: parseInt(pagination.offset) || 0,
      },
    };
  },
});

export const getPlanById = (planId, token) => ({
  endpoint: `/api/v1/recurring-payments/plans/${planId}`,
  method: 'GET',
  headers: {
    'X-Authentication-Token': token,
  },
  cache: false,
  proxy: false,
  skipErrorDisplay: true,
  preserveErrorObject: true,
  parseResponse: (response) => {
    const apiData = response.data?.data;
    return mapPlanFromApi(apiData);
  },
});

export const createPlan = (data, imageContent, imageFileName, backgroundColor, token) => {
  const payload = { ...data };
  
  if (imageContent) {
    payload.image_content = imageContent;
    payload.image_file_name = imageFileName || 'plan-image.png';
  }
  
  if (backgroundColor) {
    payload.background_color = backgroundColor;
  }
  
  return {
    endpoint: '/api/v1/recurring-payments/plans',
    method: 'POST',
    data: payload,
    headers: {
      'X-Authentication-Token': token,
    },
    proxy: false,
    skipErrorDisplay: true,
    preserveErrorObject: true,
    parseResponse: (response) => {
      const apiData = response.data?.data;
      return mapPlanFromApi(apiData);
    },
  };
};

export const updatePlan = (planId, data, imageContent, imageFileName, backgroundColor, deleteImage, token) => {
  const payload = { ...data };
  
  if (imageContent) {
    payload.image_content = imageContent;
    payload.image_file_name = imageFileName || 'plan-image.png';
  }
  
  if (backgroundColor) {
    payload.background_color = backgroundColor;
  }
  
  if (deleteImage === true) {
    payload.delete_image = true;
  }
  
  return {
    endpoint: `/api/v1/recurring-payments/plans/${planId}`,
    method: 'PUT',
    data: payload,
    headers: {
      'X-Authentication-Token': token,
    },
    proxy: false,
    skipErrorDisplay: true,
    preserveErrorObject: true,
    parseResponse: (response) => {
      const apiData = response.data?.data;
      return mapPlanFromApi(apiData);
    },
  };
};

export const cancelPlan = (plan, token) => {
  const payload = {
    status: 'inactive'
  };
  
  return {
    endpoint: `/api/v1/recurring-payments/plans/${plan.planId}`,
    method: 'PUT',
    data: payload,
    headers: {
      'X-Authentication-Token': token,
    },
    proxy: false,
    preserveErrorObject: true,
    parseResponse: (response) => {
      const apiData = response.data?.data;
      return mapPlanFromApi(apiData);
    },
  };
};

// Bulk Update de montos
export const downloadBulkUpdateTemplate = (planId, token) => ({
  endpoint: `/api/v1/recurring-payments/plans/${planId}/bulk-update-template`,
  method: 'GET',
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
});

export const uploadBulkUpdate = (file, token, planId) => ({
  endpoint: `/api/v1/recurring-payments/plans/${planId}/bulk-update`,
  method: 'POST',
  formData: {
    file: file
  },
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
});

export const confirmBulkUpdate = (fileName, token, planId) => ({
  endpoint: `/api/v1/recurring-payments/plans/${planId}/bulk-update`,
  method: 'POST',
  formData: {
    confirmation: 1,
    importFileName: fileName
  },
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  preserveErrorObject: true,
});

export const sendPlanInvites = (planId, contacts, token) => ({
  endpoint: `/api/v1/recurring-payments/plans/${planId}/invites`,
  method: 'POST',
  data: {
    contacts,
  },
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  skipErrorDisplay: true,
  preserveErrorObject: true,
});

const mapMerchantMeFromApi = (merchant) => {
  if (!merchant) {
    return {
      emails: [],
      reportFrequency: 'daily',
      reportType: 'both',
      sessionRequestUrl: null,
    };
  }
  return {
    merchantId: merchant.merchant_id,
    merchantCode: merchant.merchant_code,
    emails: Array.isArray(merchant.report_emails) ? merchant.report_emails : [],
    reportFrequency: merchant.report_frequency || 'daily',
    reportType: merchant.report_type || 'both',
    sessionRequestUrl: merchant.session_request_url || null,
  };
};

export const getMerchantMe = (token) => ({
  endpoint: '/api/v1/recurring-payments/merchant/me',
  method: 'GET',
  headers: {
    'X-Authentication-Token': token,
  },
  cache: false,
  proxy: false,
  skipErrorDisplay: true,
  preserveErrorObject: true,
  parseResponse: (response) => mapMerchantMeFromApi(response.data?.data),
});

export const updateMerchantMe = (payload, bootstrap, token) => ({
  endpoint: '/api/v1/recurring-payments/merchant/me',
  method: 'PUT',
  params: {
    ...(bootstrap?.merchantName ? { merchant_name: bootstrap.merchantName } : {}),
    ...(bootstrap?.merchantFiscalId ? { merchant_fiscal_id: bootstrap.merchantFiscalId } : {}),
  },
  data: {
    report_emails: payload.report_emails,
    report_frequency: payload.report_frequency,
    report_type: payload.report_type,
  },
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  skipErrorDisplay: true,
  preserveErrorObject: true,
  parseResponse: (response) => mapMerchantMeFromApi(response.data?.data),
});

export const exportPlansReport = (token) => (payload) => ({
  endpoint: '/api/v1/recurring-payments/reports/plans/export',
  method: 'GET',
  params: payload,
  headers: {
    'X-Authentication-Token': token,
  },
  proxy: false,
  responseType: 'blob',
});
